/**
 * Data copy — the registry that says, column by column, what production is
 * allowed to send the sandbox (docs/DATA-COPY.md, ADR sandbox-data-copy).
 *
 * WHY A REGISTRY INSTEAD OF A DENYLIST. A denylist only stops what someone
 * remembered to add to it. Every table and every column here must be given a
 * class — `keep`, `fake` or `drop` — and `data-copy-classification.test.ts`
 * walks the real Drizzle schema and fails the build the moment a new table or
 * column has none. Unclassified is never silently kept: `classify()` below
 * returns `drop` for anything the registry doesn't mention, so a hole here is
 * a missing row, never a leaked one.
 *
 * This file holds the TEMPLATE's own tables only. A project adds its own
 * tables' classification in `src/core/system/data-copy-project.ts` (empty in
 * the template) — additively, without ever touching this file. See
 * `docs/DATA-COPY.md`.
 */

import type { TrackedEntity } from '@/core/history'

export type ColumnClass = 'keep' | 'fake' | 'drop'

/** Which pseudonym generator a `fake` column uses (`data-copy-fake.ts`). */
export type FakeKind = 'personName' | 'personEmail'

export interface ColumnClassification {
  class: ColumnClass
  /** Required, and only meaningful, when `class` is `'fake'`. */
  fake?: FakeKind
}

/**
 * How far back a table's rows are copied.
 *  - `all`  every row, whatever its age (e.g. the example domain records).
 *  - `none` the table is never copied (every `drop` table still declares this,
 *    so "how far back" is answered for every table, not just the kept ones).
 *  - `column` rows are kept back to `DATA_COPY_HISTORY_MONTHS` (+ `extraMonths`,
 *    for a project that needs more — a comparison app diffing against the
 *    same month a year earlier declares `extraMonths: 12`). NO UPPER BOUND:
 *    `windowCutoff` only computes the oldest row still worth keeping, so a
 *    `column` window already includes every future-dated row for free —
 *    something like a booking or departure table needing a year of history
 *    plus everything upcoming declares exactly one window, on its departure
 *    date column, with `extraMonths: 12`. Documented in docs/DATA-COPY.md.
 */
export type TableWindow =
  | { kind: 'all' }
  | { kind: 'none' }
  | { kind: 'column'; column: string; extraMonths?: number }

export interface TableClassification {
  /** The table name exactly as Postgres/Drizzle has it. */
  table: string
  window: TableWindow
  /** Every column Drizzle declares for this table, each with its class. */
  columns: Record<string, ColumnClassification>
  /**
   * True for the `organization` table only. It is never inserted by the
   * importer — one organization per app (ADR single-organization) means the sandbox already
   * has its own. Its columns are still classified, so the registry proves
   * `id`/`name` were looked at rather than missed; the importer instead reads
   * the sandbox's own organization row and remaps every imported row's
   * `organization_id` onto it (docs/DATA-COPY.md).
   */
  reconcileOrganization?: boolean
  /**
   * A row-level cut beyond column scrubbing, for a table where some ROWS are
   * fine to copy and others are not — e.g. the activity log keeps rows about
   * item changes but drops rows about people entirely, because a person's
   * name and role are the row's whole content, not one column of it (see the
   * ADR). Return `true` to keep the row. Most tables need only column rules
   * and leave this unset. Takes the full combined registry (template +
   * project) so a filter can look up another table's classification —
   * `recordChangeRowFilter` below is the reason this parameter exists.
   */
  rowFilter?: (row: Record<string, unknown>, tables: TableClassification[]) => boolean
  /** One line, for the file `docs/DATA-COPY.md` points at when someone asks "why". */
  why: string
}

export const DEFAULT_HISTORY_MONTHS = 12

/** The env var name, read in `server/env.ts`. Named once, here, so nothing typos it. */
export const DATA_COPY_HISTORY_MONTHS_VAR = 'DATA_COPY_HISTORY_MONTHS'
export const DATA_COPY_SECRET_VAR = 'DATA_COPY_SECRET'

const drop = (): ColumnClassification => ({ class: 'drop' })
const keep: ColumnClassification = { class: 'keep' }
const fakeName: ColumnClassification = { class: 'fake', fake: 'personName' }
// No template column is currently classified fakeEmail — every email-bearing
// column (user.email, invitation.email, ...) belongs to a dropped table. A
// project with its own person-like table can still use `{ class: 'fake',
// fake: 'personEmail' }` directly; see data-copy-fake.ts's `fakeEmail()`.

/**
 * Which table each tracked entity's activity-log rows are about — used only
 * by `recordChangeRowFilter` below, to decide whether a `record_change` row
 * about that kind of record may leave production at all.
 *
 * Typed by `TrackedEntity`, so adding a tracked entity is a COMPILE ERROR
 * here until someone says which table it belongs to — forgetting is
 * impossible, not merely unlikely.
 */
const historyEntityTable: Record<TrackedEntity, string> = {
  items: 'items',
  users: 'user',
  autoJoinDomains: 'auto_join_domain',
  // MCP access: token, connection and switch changes.
  mcpAccess: 'mcp_token',
  // The data dictionary's edits; field and code edits log under one entity.
  dataDictionary: 'data_field_override',
  webhooks: 'webhook_endpoint',
  // Majik's records: tables arrive in the data phase (docs/DECISIONS.md D-003).
  // Until each is classified, its activity rows are dropped — fail-closed.
  businessRules: 'business_rules',
  clients: 'clients',
  contacts: 'contacts',
  cleaners: 'cleaners',
  jobs: 'jobs',
  invoices: 'invoices',
  products: 'products_services',
  orders: 'orders',
  inspections: 'inspections',
  communications: 'communications',
}

/** Activity-log entities that are not records (the data-copy refresh's own entry). */
const NON_RECORD_ENTITIES = new Set(['system'])

/**
 * record_change's row filter, DERIVED from the rest of the registry instead
 * of a hand-picked list of entities to exclude — so a table that starts (or
 * later becomes) entirely `drop`-classified can never leak through the
 * activity log.
 *
 * A row is kept only if the table its entity names is classified and NOT
 * entirely `drop`. `user` and `auto_join_domain` are fully dropped, so rows
 * about a person or an access-control rule are cut outright. Fail-closed: an
 * entity this function cannot place — unknown, or its table unclassified — is
 * dropped, never assumed safe.
 */
function recordChangeRowFilter(
  row: Record<string, unknown>,
  tables: TableClassification[],
): boolean {
  const entity = String(row.entity)
  if (NON_RECORD_ENTITIES.has(entity)) return true
  const table = (historyEntityTable as Record<string, string | undefined>)[entity]
  if (!table) return false
  const entry = tableClassificationOf(tables, table)
  if (!entry) return false
  const everyColumnDropped = Object.values(entry.columns).every((c) => c.class === 'drop')
  return !everyColumnDropped
}

/**
 * The template's own tables. Every table in `src/adapters/db/schema.ts` and
 * `auth-schema.ts` must appear exactly once here.
 */
export const templateDataCopyTables: TableClassification[] = [
  // --- Accounts and sessions: never copied. Gideon's explicit decision — the
  // sandbox keeps whoever is reviewing it signed in, and no password hash or
  // session token from a real person ever leaves production. ---
  {
    table: 'user',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      name: drop(),
      firstName: drop(),
      lastName: drop(),
      email: drop(),
      emailVerified: drop(),
      image: drop(),
      createdAt: drop(),
      updatedAt: drop(),
    },
    why: 'No accounts or passwords are ever copied to the sandbox.',
  },
  {
    table: 'session',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      expiresAt: drop(),
      token: drop(),
      createdAt: drop(),
      updatedAt: drop(),
      ipAddress: drop(),
      userAgent: drop(),
      userId: drop(),
      activeOrganizationId: drop(),
      impersonatedBy: drop(),
    },
    why: 'A session token is a bearer credential; never copied.',
  },
  {
    table: 'account',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      accountId: drop(),
      providerId: drop(),
      userId: drop(),
      accessToken: drop(),
      refreshToken: drop(),
      idToken: drop(),
      accessTokenExpiresAt: drop(),
      refreshTokenExpiresAt: drop(),
      scope: drop(),
      password: drop(),
      createdAt: drop(),
      updatedAt: drop(),
    },
    why: 'Password hashes and OAuth tokens; never copied.',
  },
  {
    table: 'verification',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      identifier: drop(),
      value: drop(),
      expiresAt: drop(),
      createdAt: drop(),
      updatedAt: drop(),
    },
    why: 'One-time codes and tokens; never copied.',
  },
  {
    table: 'organization',
    window: { kind: 'none' },
    reconcileOrganization: true,
    columns: {
      id: keep,
      name: keep,
      slug: drop(),
      logo: drop(),
      createdAt: drop(),
      metadata: drop(),
    },
    why: 'One organization per app (ADR single-organization): the sandbox keeps its own row. Never inserted — see reconcileOrganization.',
  },
  {
    table: 'member',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      organizationId: drop(),
      userId: drop(),
      role: drop(),
      createdAt: drop(),
      deactivatedAt: drop(),
      lastActiveAt: drop(),
    },
    why: 'Membership is an account fact. No accounts are copied.',
  },
  {
    table: 'invitation',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      organizationId: drop(),
      email: drop(),
      role: drop(),
      status: drop(),
      expiresAt: drop(),
      createdAt: drop(),
      inviterId: drop(),
    },
    why: 'A pending invitation names a real email address; part of the sign-up flow, dropped like invitation_link and pending_signup.',
  },
  {
    table: 'invitation_link',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      invitationId: drop(),
      organizationId: drop(),
      tokenHash: drop(),
      createdAt: drop(),
      usedAt: drop(),
      usedByUserId: drop(),
      usedEmail: drop(),
    },
    why: 'A single-use secret token and the address it was used with; sign-up-flow data, dropped.',
  },
  {
    table: 'pending_signup',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      invitationLinkId: drop(),
      organizationId: drop(),
      email: drop(),
      firstName: drop(),
      lastName: drop(),
      passwordHash: drop(),
      tokenHash: drop(),
      createdAt: drop(),
      expiresAt: drop(),
      usedAt: drop(),
    },
    why: 'A password hash waiting to be confirmed; sign-up-flow data, dropped.',
  },
  {
    table: 'auto_join_domain',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      organizationId: drop(),
      domain: drop(),
      role: drop(),
      createdAt: drop(),
      createdByUserId: drop(),
      createdByName: drop(),
    },
    why: "Who gets automatic access, and at what role, is an access-control decision for THIS deployment. The sandbox must never inherit production's rules — it keeps whatever domains an Admin has set on it directly (Settings → Users), same as it keeps its own accounts and roles.",
  },
  {
    table: 'email_change',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      organizationId: drop(),
      userId: drop(),
      oldEmail: drop(),
      newEmail: drop(),
      requestedByUserId: drop(),
      requestedByName: drop(),
      tokenHash: drop(),
      createdAt: drop(),
      expiresAt: drop(),
      confirmedAt: drop(),
      canceledAt: drop(),
    },
    why: 'Real old and new email addresses; dropped like every other account fact.',
  },
  {
    table: 'assistant_conversation',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      organizationId: drop(),
      userId: drop(),
      title: drop(),
      createdAt: drop(),
      updatedAt: drop(),
      deletedAt: drop(),
    },
    why: 'Private to the person who had it; tied to a userId no sandbox account will ever have.',
  },
  {
    table: 'assistant_message',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      conversationId: drop(),
      organizationId: drop(),
      userId: drop(),
      role: drop(),
      content: drop(),
      proposal: drop(),
      inputTokens: drop(),
      outputTokens: drop(),
      cacheReadTokens: drop(),
      cacheWriteTokens: drop(),
      createdAt: drop(),
    },
    why: 'Conversation content, often about real records and real people; dropped with its conversation.',
  },
  {
    table: 'app_setting',
    window: { kind: 'none' },
    columns: { key: drop(), value: drop(), updatedAt: drop() },
    why: "Settings an admin changed, some of them effectively secrets (the assistant's cap, prices). The sandbox keeps its own.",
  },
  // --- MCP (ADR mcp-server): credentials, approvals and the usage log. Never copied. ---
  {
    table: 'oauth_client',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      clientId: drop(),
      clientSecret: drop(),
      clientDiscoveryId: drop(),
      disabled: drop(),
      skipConsent: drop(),
      enableEndSession: drop(),
      subjectType: drop(),
      scopes: drop(),
      clientCredentialsScopes: drop(),
      userId: drop(),
      createdAt: drop(),
      updatedAt: drop(),
      name: drop(),
      uri: drop(),
      icon: drop(),
      contacts: drop(),
      tos: drop(),
      policy: drop(),
      softwareId: drop(),
      softwareVersion: drop(),
      softwareStatement: drop(),
      redirectUris: drop(),
      postLogoutRedirectUris: drop(),
      backchannelLogoutUri: drop(),
      backchannelLogoutSessionRequired: drop(),
      tokenEndpointAuthMethod: drop(),
      applicationType: drop(),
      jwks: drop(),
      jwksUri: drop(),
      grantTypes: drop(),
      responseTypes: drop(),
      requirePKCE: drop(),
      dpopBoundAccessTokens: drop(),
      referenceId: drop(),
      metadata: drop(),
    },
    why: 'AI apps registered for MCP sign-in (ADR mcp-server); some hold a client secret. The sandbox registers its own.',
  },
  {
    table: 'oauth_resource',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      identifier: drop(),
      name: drop(),
      accessTokenTtl: drop(),
      refreshTokenTtl: drop(),
      signingAlgorithm: drop(),
      signingKeyId: drop(),
      allowedScopes: drop(),
      customClaims: drop(),
      dpopBoundAccessTokensRequired: drop(),
      disabled: drop(),
      createdAt: drop(),
      updatedAt: drop(),
      policyVersion: drop(),
      metadata: drop(),
    },
    why: 'The /mcp resource this environment issues tokens for — its own address, seeded at start-up.',
  },
  {
    table: 'oauth_client_resource',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      clientId: drop(),
      resourceId: drop(),
      metadata: drop(),
      createdAt: drop(),
    },
    why: 'Which app may ask for which resource; meaningless without the apps.',
  },
  {
    table: 'oauth_refresh_token',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      token: drop(),
      clientId: drop(),
      sessionId: drop(),
      userId: drop(),
      referenceId: drop(),
      authorizationCodeId: drop(),
      resources: drop(),
      requestedUserInfoClaims: drop(),
      expiresAt: drop(),
      createdAt: drop(),
      revoked: drop(),
      rotatedAt: drop(),
      rotationReplayResponse: drop(),
      rotationReplayExpiresAt: drop(),
      authTime: drop(),
      confirmation: drop(),
      scopes: drop(),
    },
    why: 'A refresh token is a bearer credential; never copied.',
  },
  {
    table: 'oauth_access_token',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      token: drop(),
      clientId: drop(),
      sessionId: drop(),
      userId: drop(),
      referenceId: drop(),
      authorizationCodeId: drop(),
      resources: drop(),
      requestedUserInfoClaims: drop(),
      refreshId: drop(),
      expiresAt: drop(),
      createdAt: drop(),
      revoked: drop(),
      confirmation: drop(),
      scopes: drop(),
    },
    why: 'An access token is a bearer credential; never copied.',
  },
  {
    table: 'oauth_consent',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      clientId: drop(),
      userId: drop(),
      referenceId: drop(),
      resources: drop(),
      requestedUserInfoClaims: drop(),
      scopes: drop(),
      createdAt: drop(),
      updatedAt: drop(),
    },
    why: 'Which person approved which AI app — tied to production accounts, which are never copied.',
  },
  {
    table: 'oauth_client_assertion',
    window: { kind: 'none' },
    columns: { id: drop(), expiresAt: drop() },
    why: 'Replay tombstones for client assertions; meaningless anywhere else.',
  },
  {
    table: 'mcp_token',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      organizationId: drop(),
      userId: drop(),
      name: drop(),
      tokenHash: drop(),
      shown: drop(),
      createdAt: drop(),
      expiresAt: drop(),
      lastUsedAt: drop(),
      revokedAt: drop(),
    },
    why: 'Personal access tokens for MCP — credentials, even hashed, and tied to production accounts. Never copied.',
  },
  {
    table: 'mcp_call',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      organizationId: drop(),
      userId: drop(),
      userName: drop(),
      credential: drop(),
      clientName: drop(),
      tool: drop(),
      args: drop(),
      rows: drop(),
      truncated: drop(),
      linkId: drop(),
      ms: drop(),
      outcome: drop(),
      message: drop(),
      at: drop(),
    },
    why: 'The MCP usage log — who asked what, by name. Production’s own record; the sandbox keeps its own.',
  },
  {
    table: 'user_preference',
    window: { kind: 'none' },
    columns: { userId: drop(), key: drop(), value: drop(), updatedAt: drop() },
    why: "A person's own UI settings, tied to a userId no sandbox account will ever have.",
  },

  // --- Operational bookkeeping: meaningless, or actively misleading, copied
  // into a different environment. Dropped, not faked — there is nothing in
  // them worth a reviewer seeing, and re-deriving them in the sandbox (a
  // fresh price, a fresh meter check) is exactly what should happen there. ---
  {
    table: 'service_usage',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      serviceId: drop(),
      metricId: drop(),
      period: drop(),
      value: drop(),
      updatedAt: drop(),
    },
    why: "Production's own vendor usage; the sandbox meters nothing (docs/ENVIRONMENTS.md) and must not show production's bill as its own.",
  },
  {
    table: 'service_meter_check',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      serviceId: drop(),
      metricId: drop(),
      checkedAt: drop(),
      status: drop(),
      vendorValue: drop(),
      detail: drop(),
    },
    why: 'Same reasoning as service_usage.',
  },
  {
    table: 'service_metric_start',
    window: { kind: 'none' },
    columns: { id: drop(), serviceId: drop(), metricId: drop(), startedAt: drop() },
    why: 'Same reasoning as service_usage.',
  },
  {
    table: 'service_price',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      serviceId: drop(),
      metricId: drop(),
      planName: drop(),
      monthlyFeeCents: drop(),
      perUnitMicroCents: drop(),
      includedFree: drop(),
      checkedOn: drop(),
      updatedAt: drop(),
    },
    why: 'Prices set by hand on System → Services; the sandbox is set up separately, if at all.',
  },
  {
    table: 'spend_statement',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      period: drop(),
      serviceId: drop(),
      totalMicroCents: drop(),
      detail: drop(),
      capturedAt: drop(),
    },
    why: "Production's own billing history; not the sandbox's to show.",
  },
  {
    table: 'deploy_log',
    window: { kind: 'none' },
    columns: { commitSha: drop(), firstLiveAt: drop() },
    why: 'One row per commit that went live in THIS environment; meaningless copied into another.',
  },
  {
    table: 'impersonation_log',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      organizationId: drop(),
      sessionId: drop(),
      adminUserId: drop(),
      adminName: drop(),
      targetUserId: drop(),
      targetName: drop(),
      startedAt: drop(),
      expiresAt: drop(),
      endedAt: drop(),
    },
    why: "Real admins' and staff's names, and who acted as whom — dropped rather than faked, for the same reason record_change drops its users rows (see the ADR). The sandbox's own impersonations show up on its own System → Impersonation regardless.",
  },
  // Webhooks (ADR inbound-webhooks): never copied. The sandbox makes its own —
  // a production address hash in the sandbox would be useless at best — and
  // deliveries hold the message as sent, personal details and all.
  {
    table: 'webhook_endpoint',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      organizationId: drop(),
      name: drop(),
      type: drop(),
      version: drop(),
      enabled: drop(),
      secretHash: drop(),
      secretHint: drop(),
      secretSealed: drop(),
      createdAt: drop(),
      createdBy: drop(),
      rotatedAt: drop(),
      expiresAt: drop(),
      deletedAt: drop(),
      lastReceivedAt: drop(),
      lastFailureAt: drop(),
      lastDataProblemAt: drop(),
      alertEnabled: drop(),
      alertAfterValue: drop(),
      alertAfterUnit: drop(),
      alertStart: drop(),
      alertEnd: drop(),
      alertZone: drop(),
      alertDays: drop(),
    },
    why: 'Webhooks are per environment: the sandbox sets up its own, with its own address. Never copied.',
  },
  {
    table: 'webhook_delivery',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      organizationId: drop(),
      endpointId: drop(),
      receivedAt: drop(),
      outcome: drop(),
      isTest: drop(),
      itemCount: drop(),
      inserted: drop(),
      updated: drop(),
      rejected: drop(),
      rejections: drop(),
      error: drop(),
      checks: drop(),
      rerunOf: drop(),
      resolvedAt: drop(),
      resolvedBy: drop(),
      rawBody: drop(),
    },
    why: 'The message as sent, personal details included, and tied to production webhooks. Never copied.',
  },
  {
    table: 'webhook_alert_flag',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      organizationId: drop(),
      endpointId: drop(),
      flaggedAt: drop(),
      rule: drop(),
      lastDataAt: drop(),
      clearedAt: drop(),
      clearedBy: drop(),
      reportedAt: drop(),
    },
    why: 'The no-data alert’s flag history belongs to production’s own webhooks, which are never copied. The sandbox keeps its own.',
  },

  // --- The example domain and its history: what makes the sandbox look
  // populated for review. ---
  {
    table: 'items',
    window: { kind: 'all' },
    columns: {
      id: keep,
      organizationId: keep,
      title: keep,
      description: keep,
      status: keep,
      createdAt: keep,
      updatedAt: keep, // copied as it was — the copy labels itself `skip`, so the database keeps it
      createdBy: drop(),
      deletedAt: keep,
    },
    why: 'The sample domain record. createdBy points at a real user id that will not exist in the sandbox, so it is dropped, not faked — the record still shows who touched it through its History (record_change), which fakes the name instead.',
  },

  // --- The activity log: kept, because "who did what, in what order" is a
  // large part of what makes a review copy feel real — but every row that is
  // fundamentally ABOUT a person, rather than about an item, is dropped
  // outright. See the ADR for why fake-the-actor-name was rejected as the
  // rule for the whole table. ---
  {
    table: 'record_change',
    window: { kind: 'column', column: 'at' },
    rowFilter: recordChangeRowFilter,
    columns: {
      id: keep,
      organizationId: keep,
      entity: keep,
      recordId: keep,
      recordTitle: keep,
      action: keep,
      fields: keep,
      actorUserId: drop(),
      actorName: fakeName,
      via: keep,
      impersonatorName: fakeName,
      impersonationSessionId: drop(),
      at: keep,
    },
    why: "Kept so an item's History still reads in the sandbox, with every real name replaced by a deterministic pseudonym. Rows about a record whose OWN table is entirely dropped (users, auto_join_domain) are dropped whole, not faked — recordChangeRowFilter derives this from the rest of the registry, so a future fully-dropped table cannot leak through the log even if nobody remembers to add a case here.",
  },

  // --- Record history (docs/DATA-MODEL.md): production's story of
  // production's rows. The copy windows and scrubs those rows, so their
  // history would not match them; the copy itself writes none (it labels its
  // transaction `skip`), so the sandbox's history is only what happens there. ---
  {
    table: 'record_history',
    window: { kind: 'none' },
    columns: {
      id: drop(),
      organizationId: drop(),
      entity: drop(),
      recordId: drop(),
      action: drop(),
      changes: drop(), // past values of every field, personal ones included
      source: drop(),
      channel: drop(),
      ref: drop(),
      actorUserId: drop(),
      at: drop(),
    },
    why: 'Production’s history of production’s rows. The sandbox’s copy is windowed and partly faked, so that history would not match it, and its past values include fields the copy scrubs. The sandbox keeps its own.',
  },

  // --- The data dictionary's edits (docs/DATA-MODEL.md): words about data,
  // not about people — copied so the sandbox shows the same labels as live. ---
  {
    table: 'data_field_override',
    window: { kind: 'all' },
    columns: {
      organizationId: keep, // remapped onto the sandbox's own organization on import
      id: keep, // a field id, e.g. item.title
      label: keep,
      description: keep,
      updatedAt: keep,
      updatedBy: drop(), // a production user id; no accounts are copied
    },
    why: 'Friendly names and descriptions of fields. Copied so the sandbox shows the same labels as live.',
  },
  {
    table: 'data_code_override',
    window: { kind: 'all' },
    columns: {
      organizationId: keep,
      id: keep, // a code id, e.g. item.status=done
      fieldId: keep,
      value: keep,
      label: keep,
      meaning: keep, // what a code means — written about the data, not about a person
      confidence: keep,
      evidence: keep, // where a meaning was checked; must never name a person (docs/DATA-MODEL.md)
      updatedAt: keep,
      updatedBy: drop(),
    },
    why: 'What each code means. Copied so the sandbox explains codes the way live does.',
  },
]

/**
 * Every table this app knows how to classify: the template's own, plus
 * whatever a project has added in `data-copy-project.ts`. Additive — a
 * project never edits this file.
 */
export function allDataCopyTables(
  projectTables: TableClassification[] = [],
): TableClassification[] {
  return [...templateDataCopyTables, ...projectTables]
}

/** A quick lookup from table name to its classification, or undefined if none was registered. */
export function tableClassificationOf(
  tables: TableClassification[],
  table: string,
): TableClassification | undefined {
  return tables.find((entry) => entry.table === table)
}

/**
 * A column's class. Defensive by default: a table or column the registry does
 * not mention is `'drop'`, never silently kept — belt and braces alongside
 * the schema-walk test that refuses to let one go unclassified in the first
 * place.
 */
export function classify(
  tables: TableClassification[],
  table: string,
  column: string,
): ColumnClass {
  return tableClassificationOf(tables, table)?.columns[column]?.class ?? 'drop'
}

/** Tables the registry says to copy at all — i.e. not every column is `drop`. */
export function copyableTables(tables: TableClassification[]): TableClassification[] {
  return tables.filter(
    (entry) =>
      !entry.reconcileOrganization && Object.values(entry.columns).some((c) => c.class !== 'drop'),
  )
}

/**
 * The oldest `at`-column value a row may have and still be copied, or `null`
 * when the table's window doesn't cut on age (`all`, or nothing kept at all).
 */
export function windowCutoff(
  window: TableWindow,
  historyMonths: number,
  now: Date = new Date(),
): Date | null {
  if (window.kind !== 'column') return null
  const months = historyMonths + (window.extraMonths ?? 0)
  const cutoff = new Date(now)
  cutoff.setUTCMonth(cutoff.getUTCMonth() - months)
  return cutoff
}
